package com.rosario.juridico.ui.screens

import androidx.compose.animation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.navigation.NavController
import com.rosario.juridico.navigation.Routes
import com.rosario.juridico.platform.FirebaseProxy
import com.rosario.juridico.domain.AppUsageManager
import com.rosario.juridico.platform.AuthResult
import com.rosario.juridico.platform.AuthErrorCode
import com.rosario.juridico.premium.PremiumManager
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PantallaLogin(navController: NavController, initialRegisterMode: Boolean = false) {
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var repeatPassword by remember { mutableStateOf("") }
    var loading by remember { mutableStateOf(false) }
    var isRegisterMode by remember { mutableStateOf(initialRegisterMode) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    
    var passwordVisible by remember { mutableStateOf(false) }
    var repeatPasswordVisible by remember { mutableStateOf(false) }

    // Estado para flujo de Verificación de Email
    var showVerificationView by remember { mutableStateOf(false) }
    var verificationLoading by remember { mutableStateOf(false) }
    var verificationMessage by remember { mutableStateOf<String?>(null) }
    var isVerificationError by remember { mutableStateOf(false) }

    // Estados para Recuperación de Contraseña
    var showRecoveryDialog by remember { mutableStateOf(false) }
    var recoveryEmail by remember { mutableStateOf("") }
    var recoveryLoading by remember { mutableStateOf(false) }
    var recoveryMessage by remember { mutableStateOf<String?>(null) }
    var isRecoveryError by remember { mutableStateOf(false) }

    // Estados para Olvidé mi Usuario
    var showForgotUserDialog by remember { mutableStateOf(false) }

    val scope = rememberCoroutineScope()

    // Comprobar si ya está logueado pero no verificado al entrar.
    // REMOVIDO: No forzamos la vista de verificación al entrar para evitar atrapar usuarios.
    // La verificación se manejará post-registro o mediante banners en el Dashboard.
    LaunchedEffect(Unit) {
        // showVerificationView = false
    }

    // Validaciones
    val emailValido = email.contains("@") && email.contains(".")
    val passwordLargoValido = password.length >= 6
    val passwordsCoinciden = password == repeatPassword && password.isNotEmpty()
    
    val formValido = if (isRegisterMode) {
        emailValido && passwordLargoValido && passwordsCoinciden
    } else {
        emailValido && password.isNotEmpty()
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { 
                    Text(
                        if (showVerificationView) "Verificar Cuenta" 
                        else if (isRegisterMode) "Crear Cuenta" 
                        else "Iniciar Sesión"
                    ) 
                },
                navigationIcon = {
                    IconButton(onClick = { 
                        if (showVerificationView) {
                            scope.launch {
                                FirebaseProxy.logout()
                                PremiumManager.clearSession()
                                showVerificationView = false
                            }
                        } else {
                            navController.popBackStack()
                        }
                    }) {
                        Icon(Icons.Default.ArrowBack, contentDescription = "Atrás")
                    }
                }
            )
        }
    ) { padding ->
        if (showVerificationView) {
            val userEmailForDisplay = remember { FirebaseProxy.getUserEmail() ?: email }
            VerificationContent(
                padding = padding,
                userEmail = userEmailForDisplay,
                onResend = {
                    if (verificationLoading) return@VerificationContent
                    scope.launch {
                        verificationLoading = true
                        verificationMessage = null
                        val result = FirebaseProxy.sendEmailVerification()
                        if (result is AuthResult.Success) {
                            isVerificationError = false
                            verificationMessage = "Email de verificación reenviado a $userEmailForDisplay"
                        } else {
                            isVerificationError = true
                            verificationMessage = "Error al reenviar. Intenta de nuevo más tarde."
                        }
                        delay(4000)
                        verificationMessage = null
                        verificationLoading = false
                    }
                },
                onCheckStatus = {
                    if (verificationLoading) return@VerificationContent
                    scope.launch {
                        verificationLoading = true
                        FirebaseProxy.reloadUser()
                        if (FirebaseProxy.isEmailVerified()) {
                            // Redirección Seamless UX post-login
                            val targetRoute = AppUsageManager.pendingRoute
                            if (targetRoute != null) {
                                AppUsageManager.pendingRoute = null
                                navController.navigate(targetRoute) {
                                    popUpTo(Routes.LOGIN) { inclusive = true }
                                }
                            } else {
                                navController.navigate(Routes.INICIO) {
                                    popUpTo(Routes.LOGIN) { inclusive = true }
                                }
                            }
                        } else {
                            isVerificationError = true
                            verificationMessage = "Tu cuenta aún no ha sido verificada. Revisa tu correo."
                            delay(3000)
                            verificationMessage = null
                        }
                        verificationLoading = false
                    }
                },
                onLogout = {
                    if (verificationLoading) return@VerificationContent
                    scope.launch {
                        FirebaseProxy.logout()
                        PremiumManager.clearSession()
                        showVerificationView = false
                        // Resetear campos para permitir nuevo registro/login
                        email = ""
                        password = ""
                        repeatPassword = ""
                    }
                },
                isLoading = verificationLoading,
                message = verificationMessage,
                isError = isVerificationError
            )
        } else {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(padding)
                    .padding(24.dp)
                    .verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.Center,
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Text(
                    if (isRegisterMode) "¡Únete a nosotros!" else "¡Bienvenido de nuevo!",
                    style = MaterialTheme.typography.headlineMedium,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.primary
                )
                
                Text(
                    text = if (isRegisterMode) "Regístrate gratis y obtén 5 usos de prueba para todas las herramientas"
                           else "Ingresa tus datos para continuar",
                    style = MaterialTheme.typography.bodyMedium,
                    color = Color.Gray,
                    modifier = Modifier.padding(bottom = 32.dp)
                )

                // 0. BOTÓN GOOGLE
                Button(
                    onClick = {
                        loading = true
                        errorMessage = null
                        com.rosario.juridico.launchGoogleSignIn { idToken ->
                            if (idToken != null) {
                                scope.launch {
                                    try {
                                        val result = FirebaseProxy.signInWithGoogle(idToken)
                                        if (result is AuthResult.Success) {
                                            FirebaseProxy.initializeUserIfNeeded(result.uid)
                                            
                                            // Redirección Seamless UX post-login
                                            val targetRoute = AppUsageManager.pendingRoute
                                            if (targetRoute != null) {
                                                AppUsageManager.pendingRoute = null
                                                navController.navigate(targetRoute) {
                                                    popUpTo(Routes.LOGIN) { inclusive = true }
                                                }
                                            } else {
                                                navController.navigate(Routes.INICIO) {
                                                    popUpTo(Routes.LOGIN) { inclusive = true }
                                                }
                                            }
                                        } else {
                                            errorMessage = "Error al conectar con Google"
                                        }
                                    } catch (e: Exception) {
                                        errorMessage = "Error en el inicio de sesión: ${e.message}"
                                    } finally {
                                        loading = false
                                    }
                                }
                            } else {
                                loading = false
                            }
                        }
                    },
                    modifier = Modifier.fillMaxWidth().height(54.dp),
                    shape = RoundedCornerShape(16.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = Color.White,
                        contentColor = MaterialTheme.colorScheme.primary
                    ),
                    elevation = ButtonDefaults.buttonElevation(defaultElevation = 2.dp),
                    enabled = !loading
                ) {
                    Icon(Icons.Default.AccountCircle, null, modifier = Modifier.size(24.dp))
                    Spacer(Modifier.width(12.dp))
                    Text("Continuar con Google", fontWeight = FontWeight.Bold)
                }

                Spacer(Modifier.height(24.dp))

                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
                    HorizontalDivider(modifier = Modifier.weight(1f), color = Color.LightGray.copy(alpha = 0.5f))
                    Text(" o ", modifier = Modifier.padding(horizontal = 8.dp), color = Color.Gray, fontSize = 14.sp)
                    HorizontalDivider(modifier = Modifier.weight(1f), color = Color.LightGray.copy(alpha = 0.5f))
                }

                Spacer(Modifier.height(24.dp))

                // 1. EMAIL
                OutlinedTextField(
                    value = email,
                    onValueChange = { email = it; errorMessage = null },
                    label = { Text("Email") },
                    placeholder = { Text("ejemplo@correo.com") },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp),
                    leadingIcon = { Icon(Icons.Default.Email, null) },
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
                    singleLine = true,
                    isError = email.isNotEmpty() && !emailValido
                )
                if (email.isNotEmpty() && !emailValido) {
                    Text("Introduce un email válido", color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.labelSmall, modifier = Modifier.align(Alignment.Start).padding(start = 16.dp, top = 4.dp))
                }

                // 2. ELIMINADO: Se remueve el campo teléfono para simplificar el flujo

                Spacer(Modifier.height(16.dp))

                // 3. CONTRASEÑA
                OutlinedTextField(
                    value = password,
                    onValueChange = { password = it; errorMessage = null },
                    label = { Text("Contraseña") },
                    placeholder = { Text("Mínimo 6 caracteres") },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp),
                    leadingIcon = { Icon(Icons.Default.Lock, null) },
                    trailingIcon = {
                        IconButton(onClick = { passwordVisible = !passwordVisible }) {
                            Icon(if (passwordVisible) Icons.Default.Visibility else Icons.Default.VisibilityOff, null)
                        }
                    },
                    visualTransformation = if (passwordVisible) VisualTransformation.None else PasswordVisualTransformation(),
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password),
                    singleLine = true,
                    isError = isRegisterMode && password.isNotEmpty() && !passwordLargoValido
                )
                if (isRegisterMode && password.isNotEmpty() && !passwordLargoValido) {
                    Text("La contraseña debe tener al menos 6 caracteres", color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.labelSmall, modifier = Modifier.align(Alignment.Start).padding(start = 16.dp, top = 4.dp))
                }

                // OPCIONES DE RECUPERACIÓN (Solo en Login)
                if (!isRegisterMode) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        TextButton(onClick = { showForgotUserDialog = true }) {
                            Text("Olvidé mi usuario", style = MaterialTheme.typography.labelLarge)
                        }
                        TextButton(onClick = { 
                            recoveryEmail = email
                            recoveryMessage = null
                            showRecoveryDialog = true 
                        }) {
                            Text("Olvidé mi contraseña", style = MaterialTheme.typography.labelLarge)
                        }
                    }
                }

                // 4. REPETIR CONTRASEÑA (Solo en Registro)
                if (isRegisterMode) {
                    Spacer(Modifier.height(16.dp))
                    OutlinedTextField(
                        value = repeatPassword,
                        onValueChange = { repeatPassword = it; errorMessage = null },
                        label = { Text("Repetir contraseña") },
                        placeholder = { Text("Confirma tu contraseña") },
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp),
                        leadingIcon = { Icon(Icons.Default.Lock, null) },
                        trailingIcon = {
                            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.padding(end = 8.dp)) {
                                if (passwordsCoinciden && repeatPassword.isNotEmpty()) {
                                    Icon(Icons.Default.CheckCircle, "Coinciden", tint = Color(0xFF4CAF50)) // VerdeOk
                                    Spacer(Modifier.width(8.dp))
                                }
                                IconButton(onClick = { repeatPasswordVisible = !repeatPasswordVisible }) {
                                    Icon(if (repeatPasswordVisible) Icons.Default.Visibility else Icons.Default.VisibilityOff, null)
                                }
                            }
                        },
                        visualTransformation = if (repeatPasswordVisible) VisualTransformation.None else PasswordVisualTransformation(),
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password),
                        singleLine = true,
                        isError = repeatPassword.isNotEmpty() && !passwordsCoinciden
                    )
                    if (repeatPassword.isNotEmpty() && !passwordsCoinciden) {
                        Text("Las contraseñas no coinciden", color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.labelSmall, modifier = Modifier.align(Alignment.Start).padding(start = 16.dp, top = 4.dp))
                    }
                }

                if (errorMessage != null) {
                    Surface(
                        color = MaterialTheme.colorScheme.errorContainer,
                        shape = RoundedCornerShape(8.dp),
                        modifier = Modifier.padding(top = 16.dp).fillMaxWidth()
                    ) {
                        Text(
                            errorMessage!!,
                            color = MaterialTheme.colorScheme.onErrorContainer,
                            fontSize = 13.sp,
                            modifier = Modifier.padding(12.dp)
                        )
                    }
                }

                Spacer(Modifier.height(32.dp))

                Button(
                    onClick = {
                        if (loading) return@Button
                        loading = true // Bloquear inmediatamente
                        scope.launch {
                            try {
                                errorMessage = null
                                val result = if (isRegisterMode) {
                                    FirebaseProxy.signUp(email, password)
                                } else {
                                    FirebaseProxy.signIn(email, password)
                                }

                                when (result) {
                                    is AuthResult.Success -> {
                                        FirebaseProxy.initializeUserIfNeeded(result.uid)
                                        
                                        // Disparar verificación de email si es registro nuevo
                                        if (isRegisterMode) {
                                            FirebaseProxy.sendEmailVerification()
                                        }

                                        // Decisión de verificación:
                                        // 1. Si ya está verificado -> Ir al Inicio (o Ruta Pendiente)
                                        // 2. Si es Registro Nuevo -> Mostrar Pantalla Verificación
                                        // 3. Si es Login Existente -> Ir al Inicio (el Dashboard manejará la gracia)
                                        if (FirebaseProxy.isEmailVerified() || !isRegisterMode) {
                                            // Redirección Seamless UX post-login
                                            val targetRoute = AppUsageManager.pendingRoute
                                            if (targetRoute != null) {
                                                AppUsageManager.pendingRoute = null
                                                navController.navigate(targetRoute) {
                                                    popUpTo(Routes.LOGIN) { inclusive = true }
                                                }
                                            } else {
                                                navController.navigate(Routes.INICIO) {
                                                    popUpTo(Routes.LOGIN) { inclusive = true }
                                                }
                                            }
                                        } else {
                                            showVerificationView = true
                                        }
                                    }
                                    is AuthResult.Error -> {
                                        errorMessage = when (result.code) {
                                            AuthErrorCode.EMAIL_ALREADY_IN_USE -> "Este correo ya está registrado."
                                            AuthErrorCode.WRONG_PASSWORD -> "Contraseña incorrecta."
                                            AuthErrorCode.USER_NOT_FOUND -> "No existe una cuenta con este correo."
                                            AuthErrorCode.INVALID_EMAIL -> "Email inválido."
                                            AuthErrorCode.INVALID_CREDENTIALS -> "Credenciales incorrectas."
                                            AuthErrorCode.NETWORK_ERROR -> "Error de conexión."
                                            else -> "Error inesperado: ${result.message}"
                                        }
                                        if (result.code == AuthErrorCode.EMAIL_ALREADY_IN_USE) {
                                            isRegisterMode = false
                                        }
                                    }
                                }
                            } finally {
                                loading = false
                            }
                        }
                    },
                    modifier = Modifier.fillMaxWidth().height(54.dp),
                    shape = RoundedCornerShape(16.dp),
                    enabled = formValido && !loading
                ) {
                    if (loading) {
                        CircularProgressIndicator(color = Color.White, modifier = Modifier.size(24.dp), strokeWidth = 2.dp)
                    } else {
                        Text(if (isRegisterMode) "Registrarse" else "Ingresar", fontWeight = FontWeight.Bold)
                    }
                }

                TextButton(
                    onClick = { 
                        isRegisterMode = !isRegisterMode
                        errorMessage = null 
                        password = ""
                        repeatPassword = ""
                    },
                    modifier = Modifier.padding(top = 16.dp)
                ) {
                    Text(if (isRegisterMode) "¿Ya tienes cuenta? Inicia sesión" else "¿No tienes cuenta? Regístrate")
                }
            }
        }
    }

    // DIÁLOGOS (Recuperación de Contraseña)
    if (showRecoveryDialog) {
        AlertDialog(
            onDismissRequest = { if (!recoveryLoading) showRecoveryDialog = false },
            title = { Text("Recuperar contraseña", fontWeight = FontWeight.Bold) },
            text = {
                Column(modifier = Modifier.fillMaxWidth()) {
                    Text("Ingresa tu email para recibir un enlace de recuperación.", style = MaterialTheme.typography.bodyMedium, modifier = Modifier.padding(bottom = 16.dp))
                    OutlinedTextField(
                        value = recoveryEmail,
                        onValueChange = { recoveryEmail = it; recoveryMessage = null },
                        label = { Text("Email") },
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp),
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
                        singleLine = true,
                        enabled = !recoveryLoading
                    )
                    AnimatedVisibility(visible = recoveryMessage != null) {
                        Text(
                            text = recoveryMessage ?: "", 
                            color = if (isRecoveryError) MaterialTheme.colorScheme.error else Color(0xFF2E7D32), 
                            style = MaterialTheme.typography.labelSmall, 
                            modifier = Modifier.padding(top = 8.dp)
                        )
                    }
                }
            },
            confirmButton = {
                Button(
                    onClick = {
                        if (recoveryLoading) return@Button
                        recoveryLoading = true
                        scope.launch {
                            try {
                                recoveryMessage = null
                                when (val res = FirebaseProxy.sendPasswordResetEmail(recoveryEmail)) {
                                    is AuthResult.Success -> { 
                                        isRecoveryError = false
                                        recoveryMessage = "Te enviamos un enlace para restablecer tu contraseña. Revisa tu correo." 
                                    }
                                    is AuthResult.Error -> { 
                                        isRecoveryError = true
                                        recoveryMessage = when (res.code) {
                                            AuthErrorCode.USER_NOT_FOUND -> "No encontramos una cuenta con ese email."
                                            AuthErrorCode.INVALID_EMAIL -> "Email inválido."
                                            else -> "Error al enviar el correo de recuperación."
                                        }
                                    }
                                }
                            } finally {
                                recoveryLoading = false
                            }
                        }
                    },
                    enabled = !recoveryLoading && recoveryEmail.contains("@"),
                    shape = RoundedCornerShape(12.dp)
                ) {
                    if (recoveryLoading) CircularProgressIndicator(modifier = Modifier.size(20.dp), color = Color.White)
                    else Text("Enviar enlace")
                }
            },
            dismissButton = { TextButton(onClick = { showRecoveryDialog = false }, enabled = !recoveryLoading) { Text("Cancelar") } },
            shape = RoundedCornerShape(24.dp)
        )
    }

    // DIÁLOGO (Olvidé mi Usuario)
    if (showForgotUserDialog) {
        AlertDialog(
            onDismissRequest = { showForgotUserDialog = false },
            title = { Text("Recuperar usuario", fontWeight = FontWeight.Bold) },
            text = {
                Column {
                    Text(
                        "En Jurídico Rosario, tu usuario es tu correo electrónico. Si no lo recuerdas, te sugerimos:",
                        style = MaterialTheme.typography.bodyMedium
                    )
                    Spacer(Modifier.height(12.dp))
                    Text("• Buscar correos de 'Firebase' o 'Jurídico Rosario' en tus bandejas de entrada.", style = MaterialTheme.typography.bodySmall)
                    Text("• Intentar con tus correos personales más frecuentes.", style = MaterialTheme.typography.bodySmall)
                    Text("• Si eres abogado registrado, contáctanos a soporte para validar tu identidad.", style = MaterialTheme.typography.bodySmall)
                    Spacer(Modifier.height(16.dp))
                    Text(
                        "Por motivos de seguridad y privacidad, no podemos mostrar los correos registrados directamente.",
                        style = MaterialTheme.typography.labelSmall,
                        color = Color.Gray
                    )
                }
            },
            confirmButton = {
                Button(onClick = { showForgotUserDialog = false }) {
                    Text("Entendido")
                }
            },
            shape = RoundedCornerShape(24.dp)
        )
    }
}

@Composable
private fun VerificationContent(
    padding: PaddingValues,
    userEmail: String,
    onResend: () -> Unit,
    onCheckStatus: () -> Unit,
    onLogout: () -> Unit,
    isLoading: Boolean,
    message: String?,
    isError: Boolean
) {
    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(padding)
            .padding(24.dp)
            .verticalScroll(rememberScrollState()),
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Icon(
            Icons.Default.MarkEmailRead,
            contentDescription = null,
            modifier = Modifier.size(100.dp),
            tint = MaterialTheme.colorScheme.primary
        )
        
        Spacer(Modifier.height(24.dp))
        
        Text(
            "Verifica tu correo",
            style = MaterialTheme.typography.headlineMedium,
            fontWeight = FontWeight.Bold,
            color = MaterialTheme.colorScheme.primary
        )
        
        Spacer(Modifier.height(16.dp))
        
        val annotatedEmail = buildAnnotatedString {
            append("Te enviamos un correo de verificación a:\n")
            withStyle(style = SpanStyle(fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.secondary, fontSize = 18.sp)) {
                append(userEmail)
            }
        }

        Text(
            text = annotatedEmail,
            style = MaterialTheme.typography.bodyLarge,
            textAlign = TextAlign.Center,
            modifier = Modifier.padding(horizontal = 8.dp)
        )
        
        Spacer(Modifier.height(24.dp))
        
        Text(
            "Por favor, revisa tu bandeja de entrada y la carpeta de spam. Haz clic en el botón del correo para activar tu cuenta.",
            style = MaterialTheme.typography.bodyMedium,
            textAlign = TextAlign.Center,
            color = Color.Gray
        )
        
        Spacer(Modifier.height(32.dp))
        
        if (isLoading) {
            CircularProgressIndicator()
        } else {
            Button(
                onClick = onCheckStatus,
                modifier = Modifier.fillMaxWidth().height(54.dp),
                shape = RoundedCornerShape(16.dp),
                elevation = ButtonDefaults.buttonElevation(4.dp)
            ) {
                Text("Ya verifiqué mi cuenta", fontWeight = FontWeight.Bold)
            }
            
            Spacer(Modifier.height(16.dp))
            
            OutlinedButton(
                onClick = onResend,
                modifier = Modifier.fillMaxWidth().height(50.dp),
                shape = RoundedCornerShape(12.dp)
            ) {
                Icon(Icons.Default.Refresh, null, modifier = Modifier.size(18.dp))
                Spacer(Modifier.width(8.dp))
                Text("Reenviar email de verificación")
            }
            
            Spacer(Modifier.height(24.dp))
            
            HorizontalDivider(modifier = Modifier.padding(vertical = 8.dp), color = Color.LightGray.copy(alpha = 0.5f))
            
            TextButton(onClick = onLogout) {
                Text("¿Email incorrecto? Cambiar correo", color = MaterialTheme.colorScheme.error)
            }
        }
        
        AnimatedVisibility(
            visible = message != null,
            enter = fadeIn() + expandVertically(),
            exit = fadeOut() + shrinkVertically()
        ) {
            Surface(
                color = if (isError) MaterialTheme.colorScheme.errorContainer else Color(0xFFE8F5E9),
                shape = RoundedCornerShape(8.dp),
                modifier = Modifier.padding(top = 24.dp).fillMaxWidth()
            ) {
                Row(
                    modifier = Modifier.padding(12.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(
                        if (isError) Icons.Default.Error else Icons.Default.CheckCircle,
                        contentDescription = null,
                        tint = if (isError) MaterialTheme.colorScheme.error else Color(0xFF2E7D32),
                        modifier = Modifier.size(20.dp)
                    )
                    Spacer(Modifier.width(12.dp))
                    Text(
                        text = message ?: "",
                        color = if (isError) MaterialTheme.colorScheme.onErrorContainer else Color(0xFF2E7D32),
                        style = MaterialTheme.typography.bodySmall
                    )
                }
            }
        }
    }
}
